import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CustomerService } from '../services/customers.service';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomerInfoSectionComponent } from '../components/customer-info-section/customer-info-section.component';
import {
  CUSTOMER_VIEW_TABS,
  CustomerViewTabKey,
} from '@app/utils/constants/customer-view.constants';
import { Customer, DialogData } from '@app/utils/interfaces/customer.interface';
import { TabsComponent } from '@app/components/shared/tabs/tabs.component';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { CustomerOrderHistorySectionComponent } from '../components/customer-order-history-section/customer-order-history-section.component';

@Component({
  selector: 'app-view-customer-dialog',
  standalone: true,
  imports: [
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    CustomerInfoSectionComponent,
    CustomerOrderHistorySectionComponent,
    TabsComponent,
  ],
  templateUrl: './view-customer-dialog.component.html',
  styleUrl: './view-customer-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewCustomerDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<ViewCustomerDialogComponent>);
  private readonly dialogData = inject<DialogData>(MAT_DIALOG_DATA);
  private readonly errDialog = inject(MatDialog);
  private readonly customerService = inject(CustomerService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly tabs = CUSTOMER_VIEW_TABS;
  protected customerInfo = signal<Customer | null>(null);
  protected activeTab = signal<CustomerViewTabKey>('info');
  protected getCustomerInfoLoading = signal(false);

  public ngOnInit(): void {
    this.viewCustomerInfo(this.dialogData);
  }

  public onClose(): void {
    this.dialogRef.close(null);
  }

  protected viewCustomerInfo(data: DialogData): void {
    this.getCustomerInfoLoading.set(true);
    const { loyaltyId, email } = data;
    const params = loyaltyId ? { loyaltyId: loyaltyId } : { email: email };

    this.customerService
      .getCustomers(params)
      .pipe(
        finalize(() => {
          this.getCustomerInfoLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.customerInfo.set(response.data.items[0]);
        },
        error: () => {
          this.openErrorModal();
          this.onClose();
        },
      });
  }

  private openErrorModal(): void {
    this.errDialog.open(StatusModalComponent, {
      data: {
        isSuccess: false,
        title: 'customers.apiErrors.title',
        description: 'customers.apiErrors.getCustomerInfo',
      },
    });
  }
}
